import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

import { logSentry } from '@gto/sentry'

import { customInstance } from '@/api/client'
import {
  COUPON_BULK_MAX_IDS,
  type CouponBulkResult,
  couponBulkResultSchema,
  type CouponBulkUpdateRequest,
} from '@/domain/coupon-bulk'

// TODO(MKT-846): temporary shim — replace with the orval-generated hook once
// PATCH /v4/affiliates/admin/partners/{partnerId}/coupons/bulk/ is on dev. The
// endpoint ships in gto-wizard#2832 and the contract below is that PR's
// `AffiliateBulkUpdateCouponsRequest` / `AffiliateBulkUpdateCouponsResponse`.
// Mirrors the shim pattern in @/api/coupon-batch-hooks.

export type BulkUpdateCouponsVariables = {
  partnerId: string
  data: CouponBulkUpdateRequest
}

/**
 * The admin Links table's bulk actions: ONE request for the whole selection,
 * however many links it holds.
 *
 * The endpoint reports the business outcome in the BODY, not the status line — a
 * call in which every coupon failed is still a 200. Two things follow:
 *
 * 1. **The response is parsed, not trusted.** `updated` / `skipped` / `failed`
 *    are the only signal for what happened, so a malformed body throws here
 *    rather than rendering as a success the caller inferred from the 2xx.
 * 2. **This function owns Sentry for a 200 that carries failures.** React
 *    Query's `onError` never fires for a 2xx, so nothing downstream would report
 *    them. Callers surface the outcome WITHOUT re-reporting
 *    (`useMutationErrorHandler`'s `showError`), or the one event is logged
 *    twice. A transport-level failure (a 422 from an over-long selection, a 5xx)
 *    still rejects and reaches Sentry through the caller's `onError`.
 *
 * A selection over the endpoint's `coupon_ids` cap is refused HERE rather than
 * sent: the endpoint would 422 the whole body, so an over-long request can only
 * ever fail — failing before the round-trip keeps the reason nameable instead of
 * arriving as a generic schema error.
 */
export const bulkUpdateCoupons = async ({
  partnerId,
  data,
}: BulkUpdateCouponsVariables): Promise<CouponBulkResult> => {
  if (data.coupon_ids.length > COUPON_BULK_MAX_IDS) {
    throw new Error(
      `bulk coupon update refused: ${data.coupon_ids.length} ids exceeds the endpoint's cap of ${COUPON_BULK_MAX_IDS}`,
    )
  }

  const response = await customInstance<unknown>({
    url: `/v4/affiliates/admin/partners/${partnerId}/coupons/bulk/`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data,
  })

  const result = couponBulkResultSchema.parse(response)

  if (result.failed.length > 0) {
    logSentry(`bulk coupon update reported failed links in a 200: `, {
      partnerId,
      updated: result.updated,
      skipped: result.skipped,
      failed: result.failed,
    })
  }

  return result
}

export const useBulkUpdateCoupons = (options?: {
  mutation?: UseMutationOptions<CouponBulkResult, unknown, BulkUpdateCouponsVariables>
}) =>
  useMutation({
    mutationFn: bulkUpdateCoupons,
    ...options?.mutation,
  })
